import type { ComponentPropsWithoutRef } from "react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "core/shadcn/components/ui/tooltip";
import { cn } from "shadcn/lib/utils";

import { gauLabel, gauTooltip } from "./gauLabels";

type GauTermProps = {
  /** When true, renders "GAUs" with the plural tooltip. */
  plural?: boolean;
  /** When set, the term becomes a new-tab link (tooltip preserved). */
  href?: string;
  className?: string;
} & Omit<ComponentPropsWithoutRef<"span">, "children">;

/**
 * Short-form billing unit label with a dotted underline and hover
 * definition, for use in JSX copy. For non-JSX contexts (CSV headers,
 * aria-labels, emails) use the plain "GAU"/"GAUs" literals — the same
 * strings are also exported as `GAU_SINGULAR`/`GAU_PLURAL` (and `gauLabel()`)
 * from `./gauLabels` if you prefer the constant.
 */
export function GauTerm({
  plural = false,
  href,
  className,
  ...rest
}: GauTermProps) {
  const termClassName = cn(
    "cursor-pointer decoration-muted-foreground/70 decoration-dotted underline-offset-4 hover:underline",
    className,
  );
  const dataTest = plural ? "gau-term-plural" : "gau-term";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {href ? (
          // Inherit the surrounding text color: the dotted underline (not
          // link-blue) is the affordance, same as the plain-span variant.
          <a
            {...rest}
            href={href}
            target="_blank"
            rel="noreferrer"
            className={cn(termClassName, "text-inherit")}
            data-test={dataTest}
          >
            {gauLabel(plural)}
          </a>
        ) : (
          <span {...rest} className={termClassName} data-test={dataTest}>
            {gauLabel(plural)}
          </span>
        )}
      </TooltipTrigger>
      {/* z-[2100] keeps the definition above the Manage-Plan pack dropdown,
          which portals to the body at zIndex 2000. */}
      <TooltipContent className="z-[2100]">{gauTooltip(plural)}</TooltipContent>
    </Tooltip>
  );
}
